import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import { ErrorNote, Loading } from "../../components/Feedback";
import { Badge, Btn, Card, ConfirmDialog, Field, Input } from "../../components/Shared";
import { useApi } from "../../useApi";
import { LEGEND, SR_ONLY, SuccessNote, formatWhen } from "./parts";

// Management -> Inactive projects (task I, IB, 2026-10-21). The daily job project-inactivity
// labels a project INACTIVE when nothing happened in it and none of its members was active for
// `projects_inactive_after_days` (set here; default 365, 0 = only the next rule), or when no active
// member is left. A label only: nothing is locked or hidden. An admin ticks several and deletes
// them after ONE summary that names what each loses (POST /api/admin/projects/bulk-delete-preview),
// confirmed by typing the number of projects (POST /api/admin/projects/bulk-delete
// {project_ids, confirm_count}); each goes through the single project delete, and what could
// not be deleted is listed with that delete's sentence. A project active again meanwhile is
// refused by the server (409 "active again"); the preview marks it {active: true} and it is
// listed as kept, not deleted.

export const REASONS = { idle: "unused", no_active_member: "no active member" };

const table = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th = { textAlign: "left", padding: "8px 10px", fontSize: 11, fontWeight: 700, color: "#4b5563",
             textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid #e5e7eb" };
const td = { padding: "8px 10px", borderBottom: "1px solid #f3f4f6", verticalAlign: "top" };

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** What one project loses, as one line. */
export function lossLine(p) {
  const parts = [];
  if (p.hosted) parts.push(`its hosted database ${p.database} with every row entered`);
  parts.push(`${plural(p.studies, "study", "studies")}, ${plural(p.datasets, "dataset", "datasets")} and ${plural(p.files, "file", "files")}`);
  parts.push(plural(p.members, "membership", "memberships"));
  if (p.backups === null) parts.push("its nightly backups (they could not be listed: the delete will refuse)");
  else if (p.backups) parts.push(plural(p.backups, "nightly backup", "nightly backups"));
  return parts.join("; ");
}

function ProjectSettings() {
  const [days, setDays] = useState("");
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get("/admin/inactivity/settings").then(s => setDays(String(s.projects_inactive_after_days))).catch(setError);
  }, []);
  const save = async e => {
    e.preventDefault();
    setError(null);
    setSaved(null);
    try {
      const s = await api.put("/admin/inactivity/settings", { projects_inactive_after_days: Number(days || 0) });
      setDays(String(s.projects_inactive_after_days));
      setSaved(s.projects_inactive_after_days);
    } catch (err) { setError(err); }
  };
  return (
    <form onSubmit={save} aria-label="Unused projects" data-field-row
          style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", margin: "0 0 12px" }}>
      <ErrorNote error={error} />
      <Field label="Mark projects inactive after (days without activity, 0 = never)">
        <Input type="number" min="0" value={days} onChange={e => setDays(e.target.value)} />
      </Field>
      <Btn type="submit" variant="secondary">Save</Btn>
      {saved !== null && <p role="status" style={{ fontSize: 13, color: "#065f46", margin: 0 }}>
        {saved === 0 ? "Only a project with no active member left is marked inactive."
          : `Projects unused for more than ${saved} days are marked inactive.`}</p>}
    </form>
  );
}

function BulkProjectDeleteDialog({ ids, onDone, onClose }) {
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.post("/admin/projects/bulk-delete-preview", { project_ids: ids }).then(setPreview).catch(setError);
  }, [ids]);
  const found = preview ? preview.filter(p => !p.missing && !p.active) : [];
  const active = preview ? preview.filter(p => p.active) : [];
  const n = found.length;
  const releases = found.reduce((s, p) => s + (p.releases || 0), 0);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post("/admin/projects/bulk-delete", { project_ids: found.map(p => p.id), confirm_count: n });
      setResult(r);
      onDone(r);
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  if (result) {
    return (
      <ConfirmDialog title="Projects deleted" tone="neutral" confirmLabel="Close" onConfirm={onClose} onClose={onClose}>
        <p role="status" style={{ margin: 0, fontSize: 14 }}>
          {plural(result.deleted.length, "project", "projects")} deleted
          {result.deleted.length ? `: ${result.deleted.map(d => d.slug).join(", ")}` : ""}.
        </p>
        {result.refused.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={LEGEND}>Not deleted</div>
            <ul data-bulk-refused style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
              {result.refused.map(r => <li key={r.id}><strong>{r.slug || `#${r.id}`}</strong>: {r.refusal}</li>)}
            </ul>
          </div>
        )}
      </ConfirmDialog>
    );
  }
  return (
    <ConfirmDialog title={`Delete ${plural(ids.length, "project", "projects")}?`}
                   confirmWord={n ? String(n) : undefined}
                   confirmLabel={`Delete ${plural(n, "project", "projects")}`} busy={busy}
                   blockers={preview && n === 0 ? ["None of the selected projects exists and is inactive any more."] : []}
                   onConfirm={confirm} onClose={onClose}>
      <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
        Each project is deleted for good with everything in it, as Delete on one project does; its members
        are removed by the delete itself. This cannot be undone.
        {releases > 0 && ` Their ${plural(releases, "release", "releases")} stay citable in Management -> Archived releases.`}
      </p>
      <ErrorNote error={error} />
      {!preview ? (!error && <Loading />) : (
        <ul data-bulk-loss style={{ margin: "12px 0 0", paddingLeft: 18, fontSize: 13 }}>
          {found.map(p => (
            <li key={p.id} style={{ marginTop: 4 }}>
              <strong>{p.slug}</strong>: {lossLine(p)}
              {p.releases > 0 && `; ${plural(p.releases, "release", "releases")} kept in the release archive`}.
            </li>
          ))}
        </ul>
      )}
      {active.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={LEGEND}>Kept ({active.length})</div>
          <ul data-bulk-active style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
            {active.map(p => <li key={p.id}><strong>{p.slug}</strong>: active again, so it is not deleted.</li>)}
          </ul>
        </div>
      )}
      {n > 0 && <p style={{ margin: "12px 0 0", fontSize: 13 }}>Type the number of projects to delete.</p>}
    </ConfirmDialog>
  );
}

export default function InactiveProjectsCard() {
  const list = useApi("/admin/projects/inactive");
  const [selected, setSelected] = useState([]);
  const [bulk, setBulk] = useState(null);
  const [notice, setNotice] = useState(null);
  const rows = list.data || [];
  const ticked = selected.filter(id => rows.some(p => p.id === id));
  const toggle = id => setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));

  return (
    <Card>
      <h2 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700 }}>Inactive projects</h2>
      <p style={{ fontSize: 13, color: "#4b5563", margin: "0 0 12px" }}>
        Once a day, a project is marked inactive when nothing happened in it and none of its members was
        active for longer than the time below, or when none of its members is active any more. Its curators
        and the administrators are told in the bell. It is only a label: nothing is locked or hidden, a hosted
        database keeps running, and any activity makes the project active again. Only an administrator deletes.
      </p>
      <ProjectSettings />
      <SuccessNote>{notice}</SuccessNote>
      {list.error && <ErrorNote error={list.error} />}
      {!list.data ? (!list.error && <Loading />) : rows.length === 0 ? (
        <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>No project is inactive.</p>
      ) : (
        <>
          <div data-page-actions style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", margin: "0 0 12px" }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14 }}>
              <input type="checkbox" checked={ticked.length === rows.length}
                     onChange={() => setSelected(ticked.length === rows.length ? [] : rows.map(p => p.id))} />
              Select all
            </label>
            <Btn variant="danger" disabled={ticked.length === 0} onClick={() => { setNotice(null); setBulk(ticked); }}>
              Delete selected ({ticked.length})
            </Btn>
          </div>
          <table className="responsive-table" style={table}>
            <caption style={{ textAlign: "left", fontSize: 12, color: "#4b5563", paddingBottom: 6 }}>
              {plural(rows.length, "inactive project", "inactive projects")}, the longest inactive first.
            </caption>
            <thead>
              <tr>
                <th scope="col" style={th}><span style={SR_ONLY}>Select</span></th>
                <th scope="col" style={th}>Project</th>
                <th scope="col" style={th}>Why</th>
                <th scope="col" style={th}>Inactive since</th>
                <th scope="col" style={th}>Last activity</th>
                <th scope="col" style={th}>Members</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(p => (
                <tr key={p.id} data-project={p.slug}>
                  <td style={td}>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} />
                      <span style={SR_ONLY}>Select {p.slug}</span>
                    </label>
                  </td>
                  <td style={td}>
                    <Link to={`/projects/${p.id}`} style={{ fontWeight: 600 }}>{p.name}</Link>
                    <div data-break style={{ color: "#4b5563", fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{p.slug}</div>
                    {p.hosted && <Badge color="blue">hosted</Badge>}
                  </td>
                  <td data-label="Why" style={td}>{REASONS[p.inactive_reason] || p.inactive_reason}</td>
                  <td data-label="Inactive since" style={td}>{formatWhen(p.inactive_since)}</td>
                  <td data-label="Last activity" style={td}>{formatWhen(p.last_activity_at) || "—"}</td>
                  <td data-label="Members" style={td}>{p.active_members} active of {p.members}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {bulk && (
        <BulkProjectDeleteDialog ids={bulk} onClose={() => setBulk(null)}
                                 onDone={r => {
                                   setSelected(s => s.filter(id => !r.deleted.some(d => d.id === id)));
                                   if (r.deleted.length) setNotice(`${plural(r.deleted.length, "project", "projects")} deleted.`);
                                   list.reload();
                                 }} />
      )}
    </Card>
  );
}
